import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Keyboard shortcuts (docs/PARITY-STEP-1-PLAN.md section 6), as the desktop's `shortcuts`
 * package: actions with the desktop's ids, groups and default keys, for what the web has;
 * keys written as the desktop writes them ("Shortcut+Shift+F", Shortcut being Ctrl, or Cmd
 * on a Mac); and the user's own keys, kept on this device. One listener on the page matches
 * key presses to actions, and whichever part of the page handles an action runs it.
 */

export type Scope = 'everywhere' | 'writing' | 'story' | 'atlas' | 'thesaurus';

export interface ShortcutAction {
  readonly id: string;
  readonly group: string;
  readonly name: string;
  readonly scope: Scope;
  readonly defaults: readonly string[];
  /**
   * The desktop's keys, when the browser keeps them for itself (F11, Ctrl+Page Up and Down)
   * so the web's `defaults` differ. The desktop's layout files still use the same ids.
   */
  readonly desktopDefaults?: readonly string[];
}

/** The desktop's `Shortcuts`, for the actions the web has; the rest arrive with their parity steps. */
export const ACTIONS: readonly ShortcutAction[] = [
  { id: 'app.help', group: 'Everywhere', name: 'Help for this view', scope: 'everywhere', defaults: ['F1'] },
  { id: 'app.settings', group: 'Everywhere', name: 'Open Settings', scope: 'everywhere', defaults: ['Shortcut+Comma'] },
  { id: 'app.diceRoller', group: 'Everywhere', name: 'Open the Dice Roller', scope: 'everywhere', defaults: ['Shortcut+Shift+D'] },
  { id: 'mode.explorer', group: 'Knowledge Modes', name: 'Explorer Mode', scope: 'everywhere', defaults: ['Alt+1'], desktopDefaults: ['Shortcut+1'] },
  { id: 'mode.writer', group: 'Knowledge Modes', name: 'Writer Mode', scope: 'everywhere', defaults: ['Alt+2'], desktopDefaults: ['Shortcut+2'] },
  { id: 'mode.gameMaster', group: 'Knowledge Modes', name: 'Game Master Mode', scope: 'everywhere', defaults: ['Alt+3'], desktopDefaults: ['Shortcut+3'] },
  { id: 'mode.architect', group: 'Knowledge Modes', name: 'Architect Mode', scope: 'everywhere', defaults: ['Alt+4'], desktopDefaults: ['Shortcut+4'] },
  { id: 'mode.research', group: 'Knowledge Modes', name: 'Research Mode', scope: 'everywhere', defaults: ['Alt+5'], desktopDefaults: ['Shortcut+5'] },
  { id: 'app.findReplace', group: 'Everywhere', name: "Search the realm's text (Find and Replace)", scope: 'everywhere', defaults: ['Shortcut+Shift+F'] },
  { id: 'writing.save', group: 'Writing', name: 'Save the scene or entry', scope: 'writing', defaults: ['Shortcut+S'] },
  { id: 'writing.check', group: 'Writing', name: 'Check spelling: the next misspelling (Check Scene / Check Entry)', scope: 'writing', defaults: ['F7'] },
  { id: 'writing.thesaurus', group: 'Writing', name: 'Thesaurus for the word at the caret', scope: 'writing', defaults: ['Shift+F7'] },
  { id: 'thesaurus.back', group: 'Thesaurus', name: 'Back to the word before', scope: 'thesaurus', defaults: ['Alt+Left'] },
  { id: 'thesaurus.forward', group: 'Thesaurus', name: 'Forward again', scope: 'thesaurus', defaults: ['Alt+Right'] },
  { id: 'story.previousScene', group: 'Story Studio', name: 'The scene before this one', scope: 'story', defaults: ['Alt+Page Up'], desktopDefaults: ['Shortcut+Page Up'] },
  { id: 'story.nextScene', group: 'Story Studio', name: 'The scene after this one', scope: 'story', defaults: ['Alt+Page Down'], desktopDefaults: ['Shortcut+Page Down'] },
  { id: 'atlas.playerFullScreen', group: 'Atlas', name: 'Player View: full screen on or off', scope: 'atlas', defaults: ['Alt+Enter'], desktopDefaults: ['F11'] },
  { id: 'story.focus', group: 'Story Studio', name: 'Write in focus mode', scope: 'story', defaults: ['Shortcut+Shift+Enter'], desktopDefaults: ['F11'] },
];

// ---- key strokes ----

export type Modifier = 'Shortcut' | 'Alt' | 'Shift' | 'Meta';
const MODIFIER_ORDER: readonly Modifier[] = ['Shortcut', 'Alt', 'Shift', 'Meta'];

export interface KeyStroke {
  readonly modifiers: readonly Modifier[];
  /** JavaFX's `KeyCode` name, as the desktop's files hold it: "S", "F7", "Comma", "Page Down". */
  readonly key: string;
}

/** `KeyStroke.parse`: modifiers in any order and case, Ctrl and Shortcut alike; null if it isn't one. */
export function parseKey(text: string): KeyStroke | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const parts: string[] = [];
  let part = '';
  for (const c of trimmed) {
    if (c === '+' && part.trim() !== '') {
      parts.push(part.trim());
      part = '';
    } else part += c;
  }
  if (part.trim() !== '') parts.push(part.trim());
  if (parts.length === 0) return null;
  const modifiers = new Set<Modifier>();
  for (const token of parts.slice(0, -1)) {
    const modifier = ({ shortcut: 'Shortcut', ctrl: 'Shortcut', control: 'Shortcut', alt: 'Alt', shift: 'Shift', meta: 'Meta', win: 'Meta', windows: 'Meta' } as const)[
      token.toLowerCase() as 'ctrl'
    ];
    if (!modifier) return null;
    modifiers.add(modifier);
  }
  // A letter in either case is the same key.
  const key = parts.at(-1)!;
  return { modifiers: MODIFIER_ORDER.filter((m) => modifiers.has(m)), key: /^[a-z]$/.test(key) ? key.toUpperCase() : key };
}

/** `KeyStroke.toString`: "Shortcut+Shift+F". */
export function formatKey(stroke: KeyStroke): string {
  return [...stroke.modifiers, stroke.key].join('+');
}

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const KEY_LABELS: Record<string, string> = {
  Comma: ',', Period: '.', Slash: '/', 'Back Slash': '\\', Semicolon: ';', Quote: "'", 'Back Quote': '`',
  'Open Bracket': '[', 'Close Bracket': ']', Minus: '-', Equals: '=', Plus: '+',
};

/** How it's shown: "Ctrl+Shift+F" (Cmd on a Mac), "Ctrl+,", "F1". */
export function keyLabel(stroke: KeyStroke): string {
  const mac = isMac();
  const names: Record<Modifier, string> = { Shortcut: mac ? 'Cmd' : 'Ctrl', Alt: mac ? 'Option' : 'Alt', Shift: 'Shift', Meta: mac ? 'Ctrl' : 'Win' };
  return [...stroke.modifiers.map((m) => names[m]), KEY_LABELS[stroke.key] ?? stroke.key].join('+');
}

const CODE_KEYS: Record<string, string> = {
  Comma: 'Comma', Period: 'Period', Slash: 'Slash', Backslash: 'Back Slash', Semicolon: 'Semicolon', Quote: 'Quote',
  Backquote: 'Back Quote', BracketLeft: 'Open Bracket', BracketRight: 'Close Bracket', Minus: 'Minus', Equal: 'Equals',
  PageUp: 'Page Up', PageDown: 'Page Down', Home: 'Home', End: 'End', Insert: 'Insert', Delete: 'Delete',
  Backspace: 'Back Space', Enter: 'Enter', NumpadEnter: 'Enter', Escape: 'Esc', Tab: 'Tab', Space: 'Space',
  ArrowLeft: 'Left', ArrowRight: 'Right', ArrowUp: 'Up', ArrowDown: 'Down',
  NumpadAdd: 'Add', NumpadSubtract: 'Subtract', NumpadMultiply: 'Multiply', NumpadDivide: 'Divide',
};

/** The key stroke a key press makes, or null for a modifier on its own. Letters by what they type, so other layouts work. */
export function strokeOf(event: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): KeyStroke | null {
  let key: string | undefined;
  if (/^F([1-9]|1[0-9]|2[0-4])$/.test(event.key)) key = event.key;
  else if (/^Key[A-Z]$/.test(event.code) || /^[a-z]$/i.test(event.key)) key = /^[a-z]$/i.test(event.key) ? event.key.toUpperCase() : event.code.slice(3);
  else if (/^Digit[0-9]$/.test(event.code)) key = event.code.slice(5);
  else if (/^Numpad[0-9]$/.test(event.code)) key = `Numpad ${event.code.slice(6)}`;
  else key = CODE_KEYS[event.code];
  if (!key) return null;
  const mac = isMac();
  const modifiers: Modifier[] = [];
  if (mac ? event.metaKey : event.ctrlKey) modifiers.push('Shortcut');
  if (event.altKey) modifiers.push('Alt');
  if (event.shiftKey) modifiers.push('Shift');
  if (mac ? event.ctrlKey : event.metaKey) modifiers.push('Meta');
  return { modifiers, key };
}

const sameStroke = (a: KeyStroke, b: KeyStroke) => formatKey(a) === formatKey(b);

/** Whether a key press is one of an action's keys: for a part of the page that handles its own keys. */
export function isPressed(event: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>, actionId: string): boolean {
  const stroke = strokeOf(event);
  return stroke !== null && keysOf(actionId).some((k) => sameStroke(k, stroke));
}

// ---- what can't be a shortcut ----

/** The desktop's reserved keys, kept for their usual meaning everywhere. */
const RESERVED = ['Esc', 'Enter', 'Tab', 'Left', 'Right', 'Up', 'Down', 'Delete', 'Back Space', 'Space', 'Shortcut+C', 'Shortcut+X', 'Shortcut+V', 'Shortcut+A', 'Shortcut+Z', 'Shortcut+Y', 'Shortcut+Shift+Z'];
/** Keys a browser keeps for itself: pages never see them, or can't stop what they do. */
const BROWSER = ['Shortcut+N', 'Shortcut+Shift+N', 'Shortcut+T', 'Shortcut+Shift+T', 'Shortcut+W', 'Shortcut+Shift+W', 'Shortcut+Tab', 'Shortcut+Shift+Tab', 'Shortcut+Q', 'Shortcut+Shift+Q', 'Alt+F4', 'Shortcut+L', 'Shortcut+R', 'Shortcut+Page Up', 'Shortcut+Page Down', 'Shortcut+1', 'Shortcut+2', 'Shortcut+3', 'Shortcut+4', 'Shortcut+5', 'Shortcut+6', 'Shortcut+7', 'Shortcut+8', 'Shortcut+9', 'F5', 'F11', 'F12', 'Shortcut+Shift+I', 'Shortcut+Shift+J'];

/** Why a key stroke can't be a shortcut, or null when it can. */
export function refusal(stroke: KeyStroke): string | null {
  const text = formatKey(stroke);
  if (RESERVED.includes(text)) return `${keyLabel(stroke)} keeps its usual meaning everywhere.`;
  if (BROWSER.includes(text)) return `The browser keeps ${keyLabel(stroke)} for itself, so Realmery can't use it.`;
  // Without Ctrl, Alt or Win, a key other than F1 to F24 would be typed where typing happens.
  const command = stroke.modifiers.some((m) => m !== 'Shift');
  if (!command && !/^F([1-9]|1[0-9]|2[0-4])$/.test(stroke.key)) return `${keyLabel(stroke)} would be typed: use Ctrl, Alt or an F key with it.`;
  return null;
}

// ---- the user's keys ----

const STORAGE_KEY = 'realmery.shortcuts';

function readStored(): Record<string, string[]> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const kept: Record<string, string[]> = {};
    for (const action of ACTIONS) {
      const keys = (parsed as Record<string, unknown>)[action.id];
      if (Array.isArray(keys) && keys.length <= 4) {
        kept[action.id] = keys.flatMap((k) => {
          const stroke = typeof k === 'string' ? parseKey(k) : null;
          return stroke && !refusal(stroke) ? [formatKey(stroke)] : [];
        });
      }
    }
    return kept;
  } catch {
    return {};
  }
}

let changed: Record<string, string[]> = readStored();
const listeners = new Set<() => void>();
let snapshot = 0;

function save(next: Record<string, string[]>): boolean {
  changed = next;
  snapshot++;
  listeners.forEach((l) => l());
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(changed));
    return true;
  } catch {
    return false;
  }
}

/** An action's keys now: the user's, or its defaults. */
export function keysOf(actionId: string): KeyStroke[] {
  const action = ACTIONS.find((a) => a.id === actionId);
  if (!action) return [];
  return (changed[actionId] ?? action.defaults).flatMap((k) => parseKey(k) ?? []);
}

export function isDefault(actionId: string): boolean {
  return changed[actionId] === undefined;
}

/** Whether two actions can be pressed in the same place, so can't share a key. */
const overlaps = (a: ShortcutAction, b: ShortcutAction) => a.scope === 'everywhere' || b.scope === 'everywhere' || a.scope === b.scope;

/** The action already using `stroke` where `actionId` works, if any. */
export function clashOf(actionId: string, stroke: KeyStroke): ShortcutAction | null {
  const action = ACTIONS.find((a) => a.id === actionId)!;
  return ACTIONS.find((other) => other.id !== actionId && overlaps(action, other) && keysOf(other.id).some((k) => sameStroke(k, stroke))) ?? null;
}

/**
 * Gives `actionId` the key `stroke` (replacing `replacing`, or added). A clash is settled as
 * the desktop does: `swap` gives the other action this one's old key, `replace` takes the key
 * from it. Returns false when this device couldn't keep the change.
 */
export function setKey(actionId: string, stroke: KeyStroke, replacing: KeyStroke | null, clash: 'swap' | 'replace' | null = null): boolean {
  const next = { ...changed };
  const mine = keysOf(actionId).filter((k) => !(replacing && sameStroke(k, replacing)) && !sameStroke(k, stroke));
  next[actionId] = [...mine, stroke].map(formatKey);
  const other = clashOf(actionId, stroke);
  if (other && clash) {
    const theirs = keysOf(other.id).filter((k) => !sameStroke(k, stroke));
    if (clash === 'swap' && replacing) theirs.push(replacing);
    next[other.id] = theirs.map(formatKey);
  }
  return save(next);
}

export function removeKey(actionId: string, stroke: KeyStroke): boolean {
  return save({ ...changed, [actionId]: keysOf(actionId).filter((k) => !sameStroke(k, stroke)).map(formatKey) });
}

export function resetAction(actionId: string): boolean {
  const { [actionId]: _gone, ...rest } = changed;
  return save(rest);
}

export function resetAll(): boolean {
  return save({});
}

/** Re-renders when the keys change. */
export function useShortcutKeys(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
  );
}

// ---- running them ----

const handlers = new Map<string, (() => void)[]>();

/** Runs `handler` when the action's keys are pressed, while the component is on the page; the newest handler wins. */
export function useShortcut(actionId: string, handler: () => void, enabled = true): void {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const run = () => latest.current();
    handlers.set(actionId, [...(handlers.get(actionId) ?? []), run]);
    return () => {
      handlers.set(actionId, (handlers.get(actionId) ?? []).filter((h) => h !== run));
    };
  }, [actionId, enabled]);
}

/** Set while the Settings page is listening for a new key, so presses aren't acted on. */
let capturing = false;
export function setCapturing(on: boolean): void {
  capturing = on;
}

/** The one listener, installed once (main.tsx). */
export function listenForShortcuts(target: Window = window): void {
  target.addEventListener('keydown', (event) => {
    if (capturing || event.defaultPrevented || event.repeat) return;
    const stroke = strokeOf(event);
    if (!stroke) return;
    for (const action of ACTIONS) {
      if (!keysOf(action.id).some((k) => sameStroke(k, stroke))) continue;
      const handler = handlers.get(action.id)?.at(-1);
      if (!handler) continue;
      event.preventDefault();
      handler();
      return;
    }
  });
}

/** For tests: reread storage as a fresh page would. */
export function reloadShortcuts(): void {
  changed = readStored();
  snapshot++;
}
